import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  HouseLine,
  UsersThree,
  Receipt,
  Bank,
  CreditCard,
  CalendarBlank,
  UserGear,
  SignOut,
  UploadSimple,
  CurrencyEurIcon,
  UserCircle,
  TrayArrowDown,
  FileCloudIcon,
  Calculator,
  Bell,
  GearSix,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { Toaster } from "sonner";
import ChangePasswordModal from "./ChangePasswordModal";

const nav = [
  { to: "/", label: "Inicio", icon: HouseLine, end: true },
  { to: "/documentos", label: "Documentos", icon: FileCloudIcon },
  { to: "/calendario", label: "Calendario", icon: CalendarBlank },
  {
    to: "/notificaciones",
    label: "Notificaciones",
    icon: Bell,
  },
  {
    to: "/censo",
    label: "Censo",
    icon: UsersThree,
    perm: PERMISSIONS.VIEW_MEMBERS,
  },
  {
    to: "/solicitudes",
    label: "Solicitudes",
    icon: TrayArrowDown,
    perm: PERMISSIONS.VIEW_MEMBERS,
  },
  {
    to: "/tarifas",
    label: "Tarifas",
    icon: CurrencyEurIcon,
    perm: PERMISSIONS.MANAGE_TARIFFS,
  },
  { to: "/recibos", label: "Recibos", icon: Receipt },
  {
    to: "/mandatos",
    label: "Mandatos",
    icon: CreditCard,
    perm: PERMISSIONS.VIEW_MANDATOS,
  },
  {
    to: "/remesas",
    label: "Remesas",
    icon: Bank,
    perm: PERMISSIONS.VIEW_REMESAS,
  },
  {
    to: "/contabilidad",
    label: "Contabilidad",
    icon: Calculator,
    perm: PERMISSIONS.MANAGE_CONTABILIDAD,
  },
  {
    to: "/importaciones",
    label: "Importaciones",
    icon: UploadSimple,
    perm: PERMISSIONS.MANAGE_IMPORTS,
  },
  {
    to: "/usuarios",
    label: "Usuarios",
    icon: UserGear,
    perm: PERMISSIONS.MANAGE_USERS,
  },
  {
    to: "/configuracion",
    label: "Configuración",
    icon: GearSix,
    perms: [
      PERMISSIONS.MANAGE_ORGANIZATION_CONFIG,
      PERMISSIONS.MANAGE_SETTINGS,
    ],
  },
];

export default function Layout() {
  const { user, logout, can, notificationsSummary } = useAuth();
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-med-bg flex" data-testid="layout-root">
      <aside
        className="w-64 bg-med-surface border-r border-med-border h-screen flex flex-col fixed left-0 top-0 px-4 py-6"
        data-testid="sidebar"
      >
        <div className="flex items-center gap-2.5 px-2 mb-10">
          <div className="w-10 h-10 rounded-xl overflow-hidden grid place-items-center">
            <img
              src="/favicon.png"
              alt="Trakka"
              className="w-full h-full object-contain"
            />
          </div>

          <div>
            <div className="font-display font-bold text-med-text leading-tight">
              Trakka
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-med-muted">
              Panel
            </div>
          </div>
        </div>

        <div className="sidebar-scroll flex-1 min-h-0 overflow-y-auto">
          <nav className="flex-1 space-y-1">
            {nav.map((item) => {
              if (item.perm && !can(item.perm)) return null;

              if (
                item.perms &&
                !item.perms.some((permission) => can(permission))
              ) {
                return null;
              }
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm font-medium ${
                      isActive
                        ? "bg-med-primary/10 text-med-primary"
                        : "text-med-text hover:bg-med-surface2"
                    }`
                  }
                >
                  <Icon size={20} weight="duotone" />
                  {item.label}
                  {item.to === "/solicitudes" &&
                    notificationsSummary?.admin_pending_requests > 0 && (
                      <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {notificationsSummary.admin_pending_requests}
                      </span>
                    )}
                </NavLink>
              );
            })}
          </nav>
        </div>

        <div className="pt-4 mt-4 border-t border-med-border">
          <div className="px-3 py-2 text-xs text-med-muted">{user?.email}</div>
          <NavLink
            to="/perfil"
            data-testid="nav-perfil"
            className={({ isActive }) =>
              `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-med-primary/10 text-med-primary"
                  : "text-med-text hover:bg-med-surface2"
              }`
            }
          >
            <UserCircle size={20} weight="duotone" />
            Mi perfil
            {notificationsSummary?.user_pending_requests > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                {notificationsSummary.user_pending_requests}
              </span>
            )}
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-med-text hover:bg-med-surface2 transition-colors"
          >
            <SignOut size={20} weight="duotone" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        <Outlet />
      </main>

      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid #E8E4DF",
            color: "#2A2725",
          },
        }}
      />
    </div>
  );
}
